import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users } from 'lucide-react';
import { MealCompositionWithRecipes } from '@/types/mealComposition';

interface CompositionImageSelectionCardProps {
  composition: MealCompositionWithRecipes;
  onSelect: () => void;
  isSubmitting: boolean;
  onTagSelect?: (tag: string) => void;
  isMobile?: boolean;
}

const CompositionImageSelectionCard: React.FC<CompositionImageSelectionCardProps> = ({ 
  composition, 
  onSelect, 
  isSubmitting, 
  onTagSelect,
  isMobile = false 
}) => {
  return (
    <Card className="h-full flex flex-col border-golden-light bg-card shadow-sm hover:shadow-md transition-shadow duration-200 hover:border-sage/30">
      <CardHeader className="pb-2">
        <CardTitle className="line-clamp-2 text-base md:text-lg font-playfair text-charcoal">
          {composition.name}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3 font-inter">
          A curated collection of {composition.recipes.length} {composition.recipes.length === 1 ? 'recipe' : 'recipes'} for a family meal.
        </p>
        
        {/* Tags Section */}
        <div className="flex flex-wrap gap-1 mt-2">
          {/* Composition Type Badge */}
          <Badge variant="outline" className="text-xs border-terracotta text-terracotta bg-terracotta-light/20">
            <Users className="h-3 w-3 mr-1" />
            Composition
          </Badge>
          
          {/* Show tags - limit on mobile */}
          {composition.tags && composition.tags.length > 0 && (
            <>
              {composition.tags.slice(0, isMobile ? 2 : composition.tags.length).map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className={`text-xs bg-golden-light text-charcoal border-golden ${onTagSelect ? 'cursor-pointer hover:bg-golden hover:text-cream transition-colors' : ''}`}
                  onClick={() => onTagSelect?.(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-2 pt-0 md:pt-2">
        <Button 
          onClick={onSelect}
          disabled={isSubmitting}
          size="sm" 
          className="w-full h-8 md:h-9 text-xs md:text-sm font-inter bg-sage text-cream hover:bg-sage/90"
        >
          {isSubmitting ? 'Selecting...' : 'Select Composition'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CompositionImageSelectionCard;